/**
 * Surveys — the questionnaires the dashboards open for people.
 *
 * Same shape as the opt-in list: cards in a collapsible section per BU, and inside each BU a light
 * level grouping the surveys by the opt-in feature they ask about. The rule worth stating is that
 * **nothing on this screen is ever added up across cards** (R21). Each card carries its own
 * counters, side by side, and the results screen refuses to open without a survey selected, for
 * the same reason.
 *
 * The feature level exists because of the exit questionnaire: an enquete and an exit questionnaire
 * about the same feature are two moments of asking about the same thing — one to whoever is using
 * it, one to whoever is leaving — and side by side with nothing saying so they read as a duplicate.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton, UTLoading, UTSelect } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteSurvey } from '../../Hooks/useDeleteSurvey';
import { useRestoreSurvey } from '../../Hooks/useRestoreSurvey';
import { useSurveys } from '../../Hooks/useSurveys';
import { useUpdateSurvey } from '../../Hooks/useUpdateSurvey';
import type { Survey, SurveyApprovalStatus } from '../../Types/Surveys.types';
import type { FeatureSurveyGroup } from '../../Utils/groupSurveysByTenant';
import { groupSurveysByTenant } from '../../Utils/groupSurveysByTenant';
import { APPROVAL_LABELS, surveyTitle } from '../../Utils/surveyFormat';
import { SurveyCard } from '../SurveyCard/SurveyCard';
import { SurveyRestoredDialog } from './Components/SurveyRestoredDialog';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { useTenants } from '@/Hooks/useTenants';
import { getTenantColor, getTenantIcon } from '@/Shared/brand';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { ConfirmationDialog } from '@/Shared/components/ConfirmationDialog/ConfirmationDialog';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { Switch } from '@/Shared/components/Switch/Switch';
import { TruncatedListNotice } from '@/Shared/components/TruncatedListNotice/TruncatedListNotice';
import { isListTruncated } from '@/Shared/constants/listLimits';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const filterStyle = css`
  min-width: 190px;
`;

const groupsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const errorStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-ml);
  border: 1px solid var(--color-danger);
  border-left: 3px solid var(--color-danger);
  border-radius: var(--radius-card);
  background: var(--color-danger-subtle);
  font-size: var(--font-size-sm);
`;

const errorTitleStyle = css`
  font-weight: var(--font-weight-semibold);
`;

const cardsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(360px, 100%), 1fr));
  gap: var(--space-base);
`;

/**
 * The feature level inside a BU.
 *
 * Two surveys about the same opt-in feature are not a hierarchy — neither contains the other — so
 * the grouping is drawn as a light heading over a normal grid of cards, not as a nested card or a
 * second collapsible. The point is only to say *these ask about the same thing*.
 */
const featureGroupsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

/**
 * What makes the cards look **held** by the feature instead of merely printed under it.
 *
 * The heading alone was a thin line floating over a grid: the eye read "a label, and then some
 * cards that happen to come next", which is exactly the question the feature level exists to
 * answer. Three cheap things fix it, and none of them is a new component — a rule down the left,
 * an indent, and a sunken ground the cards sit on. The cards keep their own surface and border, so
 * they read as objects **inside** a container rather than as a striped list.
 *
 * The rule carries the only colour: linked groups get the accent, because they are a real
 * hierarchy, and the unlinked bucket gets plain grey, because "sem funcionalidade" is the absence
 * of one and colouring it the same would promote nothing into something.
 */
const featureGroupStyle = css`
  padding: var(--space-m) var(--space-m) var(--space-m) var(--space-ml);
  border-left: 3px solid var(--color-primary);
  border-radius: 0 var(--radius-card) var(--radius-card) 0;
  background: var(--color-surface-sunken);
`;

const unlinkedGroupStyle = css`
  border-left-color: var(--color-border-strong);
`;

const featureHeadStyle = css`
  display: flex;
  align-items: baseline;
  gap: var(--space-s);
  flex-wrap: wrap;
  padding-bottom: var(--space-xs);
  border-bottom: 1px solid var(--color-divider);
  margin-bottom: var(--space-m);
`;

const featureNameStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const featureKeyStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const featureMetaStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/**
 * The "mostrar excluídas" control.
 *
 * A toggle inside the same list, not a tab: `includeDeleted` ADDS the deleted rows to the live
 * ones, and that is the point. A deleted survey is on screen because it is still holding its slug
 * — `@@unique([tenantId, slug])` does not look at `deletedAt` — and a slug being held only means
 * something next to the surveys using theirs.
 */
const showDeletedStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  white-space: nowrap;
`;

/**
 * Whether the feature level has nothing to say for this BU.
 *
 * With every survey unlinked there is no grouping to show, only a banner naming the absence of one
 * — and in most tenants that is the whole screen. The heading is dropped instead.
 */
const onlyUnlinked = (features: FeatureSurveyGroup[]): boolean =>
  features.length === 1 && features[0].featureId === null;

const ALL = 'all';

const STATUS_FILTERS: { id: string; text: string; value: string }[] = [
  { id: ALL, text: 'Todos os status', value: ALL },
  ...(['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED'] as SurveyApprovalStatus[]).map(
    status => ({
      id: status,
      text: APPROVAL_LABELS[status],
      value: status,
    }),
  ),
];

const SurveysList = (): JSX.Element => {
  const navigate = useNavigate();
  const { pages: privileges } = usePrivileges();
  const { tenants, tenantOptions } = useTenants();

  const [tenantFilter, setTenantFilter] = useState<string>(ALL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Survey | undefined>();
  const [showDeleted, setShowDeleted] = useState(false);
  /**
   * The survey that has just been restored, so the screen can say it came back off the air.
   *
   * It is not a toast: `restore` preserves `approvalStatus` and forces `isActive: false`, so the
   * card goes back to reading "Aprovada" while reaching nobody. Somebody who only saw a toast
   * would wait for answers that never come.
   */
  const [restored, setRestored] = useState<Survey | undefined>();

  const { data, isLoading, error } = useSurveys(showDeleted ? { includeDeleted: true } : {});
  const deleteSurvey = useDeleteSurvey();
  const restoreSurvey = useRestoreSurvey();
  const updateSurvey = useUpdateSurvey();

  const allSurveys = useMemo(() => data?.data ?? [], [data]);

  const groups = useMemo(() => {
    const scoped = allSurveys
      .filter(survey => tenantFilter === ALL || survey.tenantSlug === tenantFilter)
      .filter(survey => statusFilter === ALL || survey.approvalStatus === statusFilter);

    return groupSurveysByTenant(scoped);
  }, [allSurveys, statusFilter, tenantFilter]);

  const tenantFilterOptions = useMemo(
    () => [
      { id: ALL, text: 'Todos os tenants', value: ALL },
      ...tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    ],
    [tenantOptions],
  );

  if (isLoading) {
    return (
      <MainContent id='surveys-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='surveys-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Questionários que os dashboards abrem para as pessoas, agrupados pela funcionalidade
            opt-in sobre a qual perguntam. Os números de cada pesquisa ficam no cartão dela — nada
            aqui é somado entre pesquisas, porque escalas e públicos diferentes não se agregam.
          </p>
          <div className={spacerStyle} />
          <div className={filterStyle}>
            <UTSelect
              options={tenantFilterOptions}
              canSelectMultiple={false}
              hasCheckbox={false}
              defaultSelectedOptions={[tenantFilterOptions[0]]}
              inputOptions={{
                textFieldProps: {
                  id: 'surveys-filter-tenant',
                  placeholder: 'Todos os tenants',
                },
              }}
              onSelectedOptionsChange={options => {
                setTenantFilter(options.length > 0 ? String(options[0].value) : ALL);
              }}
            />
          </div>
          <div className={filterStyle}>
            <UTSelect
              options={STATUS_FILTERS}
              canSelectMultiple={false}
              hasCheckbox={false}
              defaultSelectedOptions={[STATUS_FILTERS[0]]}
              inputOptions={{
                textFieldProps: {
                  id: 'surveys-filter-status',
                  placeholder: 'Todos os status',
                },
              }}
              onSelectedOptionsChange={options => {
                setStatusFilter(options.length > 0 ? String(options[0].value) : ALL);
              }}
            />
          </div>

          <span className={showDeletedStyle}>
            <Switch
              id='surveys-show-deleted'
              checked={showDeleted}
              aria-label='Mostrar pesquisas excluídas'
              onChange={setShowDeleted}
            />
            {/* Sibling, not wrapper: `Switch` renders its own label around the input. */}
            <label htmlFor='surveys-show-deleted'>Mostrar excluídas</label>
          </span>

          {isListTruncated(data?.total, allSurveys.length) ? (
            <TruncatedListNotice
              id='surveys-truncated'
              shown={allSurveys.length}
              total={data?.total ?? allSurveys.length}
              noun='pesquisas'
            />
          ) : null}

          {privileges.BUSINESS_UNITS.SURVEYS.CREATE ? (
            <UTButton
              fitContent
              id='surveys-create'
              text='Nova pesquisa'
              icon='Arrows.Plus'
              onClick={() => {
                navigate('/surveys/new');
              }}
            />
          ) : null}
        </div>

        {error ? (
          <div id='surveys-error' className={errorStyle} role='alert'>
            <span className={errorTitleStyle}>Não foi possível carregar as pesquisas</span>
            <span>{error.message}</span>
          </div>
        ) : null}

        {!error && groups.length === 0 ? (
          <EmptyState
            id='surveys-empty'
            icon='Communication.ChatCircleText'
            title='Nenhuma pesquisa cadastrada'
            description='Monte um questionário para perguntar diretamente a quem usa o dashboard — e para ler as respostas aqui, por pesquisa e por versão.'
            action={
              privileges.BUSINESS_UNITS.SURVEYS.CREATE ? (
                <UTButton
                  fitContent
                  text='Nova pesquisa'
                  icon='Arrows.Plus'
                  onClick={() => {
                    navigate('/surveys/new');
                  }}
                />
              ) : undefined
            }
          />
        ) : null}

        {!error && groups.length > 0 ? (
          <div className={groupsStyle}>
            {groups.map(group => {
              const tenant = tenants.find(item => item.slug === group.tenantSlug);

              return (
                <CollapsibleSection
                  key={group.tenantSlug}
                  id={`surveys-section-${group.tenantSlug}`}
                  title={group.tenantSlug}
                  monoTitle
                  icon={getTenantIcon({
                    slug: group.tenantSlug,
                    iconName: tenant?.iconName,
                  })}
                  iconColor={getTenantColor(group.tenantSlug)}
                  count={group.countLabel}
                  summary={group.liveLabel}
                  open={!collapsed[group.tenantSlug]}
                  onToggle={() => {
                    setCollapsed(current => ({
                      ...current,
                      [group.tenantSlug]: !current[group.tenantSlug],
                    }));
                  }}
                >
                  <div className={featureGroupsStyle}>
                    {group.features.map(featureGroup => (
                      /*
                        No heading when the unlinked bucket is the only group: in most tenants it is
                        every survey there is, and a "Sem funcionalidade vinculada" banner over the
                        whole screen would turn the normal case into an exception. The container
                        goes with the heading — a box drawn around every survey of the BU would
                        enclose nothing, since the section already is that box.
                      */
                      <div
                        key={featureGroup.featureId ?? 'sem-funcionalidade'}
                        id={`surveys-feature-group-${featureGroup.featureKey ?? 'sem-funcionalidade'}`}
                        className={
                          onlyUnlinked(group.features)
                            ? ''
                            : `${featureGroupStyle} ${featureGroup.featureId ? '' : unlinkedGroupStyle}`
                        }
                      >
                        {onlyUnlinked(group.features) ? null : (
                          <div className={featureHeadStyle}>
                            {featureGroup.featureId ? (
                              <>
                                <GLIcon
                                  variant='Misc.LinkSimple'
                                  size={13}
                                  fill='var(--color-text-muted)'
                                />
                                <span className={featureNameStyle}>{featureGroup.featureName}</span>
                                <span className={featureKeyStyle}>{featureGroup.featureKey}</span>
                                <span className={featureMetaStyle}>· funcionalidade opt-in</span>
                              </>
                            ) : (
                              <span className={featureNameStyle}>Sem funcionalidade vinculada</span>
                            )}
                            <div className={spacerStyle} />
                            <span className={featureMetaStyle}>{featureGroup.countLabel}</span>
                          </div>
                        )}

                        <div className={cardsGridStyle}>
                          {featureGroup.surveys.map(survey => (
                            <SurveyCard
                              key={survey.id}
                              survey={survey}
                              canEdit={privileges.BUSINESS_UNITS.SURVEYS.EDIT}
                              canDelete={privileges.BUSINESS_UNITS.SURVEYS.DELETE}
                              canReadResults={privileges.BUSINESS_UNITS.SURVEYS.RESULTS}
                              onResults={target => {
                                navigate(`/surveys/${target.id}/results`);
                              }}
                              onEdit={target => {
                                navigate(`/surveys/${target.id}`);
                              }}
                              onDelete={setDeleting}
                              /*
                                Restoring rides on `survey:update`, not on a permission of its own:
                                whoever can delete can undo their own mistake.
                              */
                              onRestore={
                                privileges.BUSINESS_UNITS.SURVEYS.EDIT
                                  ? target => {
                                      restoreSurvey.mutate(target.id, {
                                        onSuccess: setRestored,
                                      });
                                    }
                                  : undefined
                              }
                              isRestoring={
                                restoreSurvey.isPending && restoreSurvey.variables === survey.id
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              );
            })}
          </div>
        ) : null}
      </div>

      {/*
        Not a toast, and not optional: a restored survey reads "Aprovada" while reaching nobody,
        and this is the only place that says so and offers the way back on the air.
      */}
      {restored ? (
        <SurveyRestoredDialog
          survey={restored}
          canActivate={privileges.BUSINESS_UNITS.SURVEYS.EDIT}
          isActivating={updateSurvey.isPending}
          onClose={() => {
            setRestored(undefined);
          }}
          onActivate={target => {
            updateSurvey.mutate(
              { id: target.id, payload: { isActive: true } },
              {
                onSuccess: () => {
                  setRestored(undefined);
                },
              },
            );
          }}
        />
      ) : null}

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {deleting ? (
        <ConfirmationDialog
          open
          title='Excluir pesquisa'
          message={`"${surveyTitle(deleting)}" sai do painel e deixa de ser entregue. As ${deleting.responseCount} respostas já coletadas continuam guardadas — apagá-las todas de uma vez é uma operação à parte, fora deste painel.`}
          confirmText='Excluir'
          cancelText='Cancelar'
          variant='danger'
          isLoading={deleteSurvey.isPending}
          onClose={() => {
            setDeleting(undefined);
          }}
          onConfirm={() => {
            deleteSurvey.mutate(deleting.id, {
              onSuccess: () => {
                setDeleting(undefined);
              },
            });
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { SurveysList };
